import { envIsMexico } from '@/constants/constants';
import { IpDataModel } from '@/models/ip-data.model';
import { BffGameItem, BffUser } from '@lucky7ventures/bff-types';

export const MEXICO_FILTERED_SHORTDESCRIPTIONS: string[] = ['playson', 'inspired', 'wazdan'];

export const CANADA_FILTERED_SHORTDESCRIPTIONS = ['High 5 Games', 'Big Time Gaming'];

export const CANADA_ONTARIO_FILTERED_SHORTDESCRIPTIONS = [
  'Gold Coin Studios',
  'INO Games',
  'Jelly Entertainment',
];

export const SWEDEN_FILTERED_SHORTDESCRIPTIONS: string[] = [
  'Avatar UX',
  'AvatarUX',
  'Gold Coin Studios',
  'INO Games',
  'SpinOn',
];

export const FINLAND_FILTERED_SHORTDESCRIPTIONS: string[] = ['Gold Coin Studios', 'INO Games'];

export const SOUTH_AFRICA_FILTERED_SHORTDESCRIPTIONS: string[] = [
  'Big Time Gaming',
  'Habanero',
  'Playson',
  'Stakelogic',
];

export const THAILAND_FILTERED_SHORTDESCRIPTIONS: string[] = ['Stakelogic'];

export const CANADA_QUEBEC_FILTERED_VENDORS = ['Play N Go'];

export const CANADA_QUEBEC_FILTERED_SHORTDESCRIPTIONS = [
  'Wazdan',
  'Bragg Studios',
  'Gamomat',
  'High 5 Games',
  'Big Time Gaming',
  'Habanero',
  'Stakelogic',
];

export const CANADA_BRITISH_COLUMBIA_FILTERED_SHORTDESCRIPTIONS = [
  'Bragg Studios',
  'Gamomat',
  'High 5 Games',
  'Big Time Gaming',
  'Habanero',
  'Stakelogic',
];

export const getFilteredProviderShortDescriptions = (
  user: BffUser | null,
  locale: string,
  gameTester: boolean,
  ipData: IpDataModel,
): string[] => {
  // If user is a gameTester, we don't filter any providers
  if (gameTester) {
    return [];
  }

  // Filtering Rules for Logged in state
  if (user && user.countryCode) {
    const countryCode = user.countryCode.toUpperCase();

    if (countryCode === 'CA') {
      if (user.subdivision.code === 'CA-QC') {
        return [...CANADA_FILTERED_SHORTDESCRIPTIONS, ...CANADA_QUEBEC_FILTERED_SHORTDESCRIPTIONS];
      }

      if (user.subdivision.code === 'CA-BC') {
        return [
          ...CANADA_FILTERED_SHORTDESCRIPTIONS,
          ...CANADA_BRITISH_COLUMBIA_FILTERED_SHORTDESCRIPTIONS,
        ];
      }

      if (user.subdivision.code === 'CA-ON') {
        return [...CANADA_FILTERED_SHORTDESCRIPTIONS, ...CANADA_ONTARIO_FILTERED_SHORTDESCRIPTIONS];
      }

      return CANADA_FILTERED_SHORTDESCRIPTIONS;
    } else if (countryCode === 'MX') {
      return MEXICO_FILTERED_SHORTDESCRIPTIONS;
    } else if (countryCode === 'SE') {
      return SWEDEN_FILTERED_SHORTDESCRIPTIONS;
    } else if (countryCode === 'FI') {
      return FINLAND_FILTERED_SHORTDESCRIPTIONS;
    } else if (countryCode === 'ZA') {
      return SOUTH_AFRICA_FILTERED_SHORTDESCRIPTIONS;
    } else if (countryCode === 'TH') {
      return THAILAND_FILTERED_SHORTDESCRIPTIONS;
    }
  }

  // Filtering Rules for Logged out state
  if (locale === 'ca' || locale === 'ca-fr') {
    // for canada locale we have several special filtering rules
    if (ipData.region === 'Quebec') {
      return [...CANADA_FILTERED_SHORTDESCRIPTIONS, ...CANADA_QUEBEC_FILTERED_SHORTDESCRIPTIONS];
    }

    if (ipData.region === 'British Columbia') {
      return [
        ...CANADA_FILTERED_SHORTDESCRIPTIONS,
        ...CANADA_BRITISH_COLUMBIA_FILTERED_SHORTDESCRIPTIONS,
      ];
    }

    if (ipData.region === 'Ontario') {
      return [...CANADA_FILTERED_SHORTDESCRIPTIONS, ...CANADA_ONTARIO_FILTERED_SHORTDESCRIPTIONS];
    }

    return CANADA_FILTERED_SHORTDESCRIPTIONS;
  } else if (locale === 'sv') {
    return SWEDEN_FILTERED_SHORTDESCRIPTIONS;
  } else if (locale === 'fi') {
    return FINLAND_FILTERED_SHORTDESCRIPTIONS;
  } else if (locale === 'za') {
    return SOUTH_AFRICA_FILTERED_SHORTDESCRIPTIONS;
  } else if (locale === 'th') {
    return THAILAND_FILTERED_SHORTDESCRIPTIONS;
  } else if (envIsMexico() || locale === 'mx') {
    return MEXICO_FILTERED_SHORTDESCRIPTIONS;
  }

  return [];
};

const includesIgnoreCase = (list: string[], value: string): boolean => {
  const normalized = value.trim().toLowerCase();
  return list.some(item => item.trim().toLowerCase() === normalized);
};

export const shouldExcludeGame = (
  game: BffGameItem,
  user: BffUser | null,
  locale: string,
  gameTester: boolean,
  ipData: IpDataModel,
): boolean => {
  // Get filtered provider names based on context
  const filteredProviders = getFilteredProviderShortDescriptions(user, locale, gameTester, ipData);

  const isProviderFiltered = includesIgnoreCase(filteredProviders, game.shortDesc);

  // Special case: Quebec also filters by vendor (but not for game testers)
  const isVendorFiltered =
    !gameTester &&
    user?.countryCode?.toUpperCase() === 'CA' &&
    user?.subdivision.code === 'CA-QC' &&
    includesIgnoreCase(CANADA_QUEBEC_FILTERED_VENDORS, game.vendor);

  const isVendorFilteredLoggedOut =
    !gameTester &&
    (locale === 'ca' || locale === 'ca-fr') &&
    ipData.region === 'Quebec' &&
    includesIgnoreCase(CANADA_QUEBEC_FILTERED_VENDORS, game.vendor);

  return isProviderFiltered || isVendorFiltered || isVendorFilteredLoggedOut;
};
